import React from "react";
import { TFunction } from "i18next";
import { Link } from "react-router-dom";
import FormatDate, { FormatDurationSeconds, getDayName, getMonthName } from "go-core/components/Formatters/FormatDate";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ListConfigFilter } from "go-list/core/components/Filter/services/types";
import { RenderColumnInArea, RenderColumnInAreaType, ReportConfigColumn } from "go-report/core/services/types";
import { getReportFilter } from "go-report/reports/services/report-service";
import { OrganizationState } from "go-security/services/organizations/types";
import { apiOrganization } from "../../services/Api/Organization/apiOrganization";
import { apiReport } from "../../services/Api/Report/apiReport";

export class ReportKdsUtils {
	static getConfigPropertiesForTimeColumns(t: TFunction, totalValueKey: string, numberOfValuesKey: string) {
		return {
			renderView: (
				item: number,
				fullAggregateObject: Record<string, any>,
				{ rowType, columnType }: RenderColumnInArea,
				renderPDF?: boolean
			) => {
				if (rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY) {
					const { [totalValueKey]: totalValue, [numberOfValuesKey]: numberOfValues } = fullAggregateObject;

					if (numberOfValues === 0) return renderPDF ? "0s" : FormatDurationSeconds(item, t, true, true);

					const averageValue = totalValue / numberOfValues;

					return FormatDurationSeconds(averageValue, t, true, true);
				}

				return FormatDurationSeconds(item, t, true, true);
			},
			renderCsv: (
				item: number,
				fullAggregateObject: Record<string, any>,
				{ rowType, columnType }: RenderColumnInArea
			) => {
				if (rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY) {
					const { [totalValueKey]: totalValue, [numberOfValuesKey]: numberOfValues } = fullAggregateObject;

					if (numberOfValues === 0) return 0;

					const averageValue = totalValue / numberOfValues;

					return `${Math.floor(averageValue / 60)}.${Number(
						((averageValue % 60) / 60).toFixed(2).slice(2, 4)
					)}`;
				}

				return `${Math.floor(item / 60)}.${Number(((item % 60) / 60).toFixed(2).slice(2, 4))}`;
			},
		};
	}

	static getSelectedColumns(): string[] {
		return [
			"average_new_time",
			"average_in_progress_time",
			"average_packing_time",
			"average_delivery_time",
			"average_total_time",
		];
	}

	static getColumns(t: TFunction): ReportConfigColumn[] {
		return [
			{
				id: "average_new_time",
				name: t("modules.preparation_status_report.field.average_new_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "new_time", "new_time_count"),
			},
			{
				id: "average_in_progress_time",
				name: t("modules.preparation_status_report.field.average_in_progress_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "in_progress_time", "in_progress_time_count"),
			},
			{
				id: "average_packing_time",
				name: t("modules.preparation_status_report.field.average_packing_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "packing_time", "packing_time_count"),
			},
			{
				id: "average_delivery_time",
				name: t("modules.preparation_status_report.field.average_delivery_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "delivery_time", "delivery_time_count"),
			},
			{
				id: "average_total_time",
				name: t("modules.preparation_status_report.field.average_total_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "total_time", "total_time_count"),
			},
		];
	}

	static getGroups(t: TFunction): Array<Record<string, any>> {
		return [
			{
				id: "EMPLOYEE",
				name: t("components.reports.groups.EMPLOYEE"),
			},
			{
				id: "ORDER_REQUESTED_PICKUP_AT",
				name: t("modules.kds_order_report.field.order_requested_pickup_at.title"),
			},
		];
	}

	static getGroupsInfo = (t: TFunction, organization?: OrganizationState) => {
		return {
			EMPLOYEE: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link to={`/${organization.id}/employees/${groupByValue.id}`} rel="noreferrer" target="_blank">
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}

				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			ORDER_REQUESTED_PICKUP_AT: (groupByValue: Record<string, any>) => {
				const { name } = groupByValue;

				if (name === "empty") return "-";

				return FormatDate(new Date(Number(name)));
			},
		};
	};

	static getOrderNumberRedirectLink = (orderName: string, organizationId?: number) => {
		if (!organizationId) return orderName;

		const redirectFilterValue = `${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}number|e=${orderName}`;
		const encodedRedirectFilter = btoa(unescape(encodeURIComponent(redirectFilterValue)));
		const redirectURL = `/${organizationId}/sales/orders?f=${encodedRedirectFilter}`;

		return (
			<Link to={redirectURL} target="_blank">
				{orderName}
			</Link>
		);
	};

	static getFilters(
		t: TFunction,
		groupReport?: boolean,
		filtersParams?: React.MutableRefObject<string>,
		type?: string
	): ListConfigFilter[] {
		return [
			{
				id: "employee",
				name: t("common.word.employee"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"EMPLOYEE",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getEmployeesSearchSelect(
									search,
									params,
									{
										cancelToken: options?.token,
									},
									t
								),
					  },
			},
			{
				id: "average_new_time",
				name: t("modules.kds_order_report.field.average_new_time.title"),
				type: "number",
			},
			{
				id: "average_in_progress_time",
				name: t("modules.kds_order_report.field.average_in_progress_time.title"),
				type: "number",
			},
			{
				id: "average_packing_time",
				name: t("modules.kds_order_report.field.average_packing_time.title"),
				type: "number",
			},
			{
				id: "date_range",
				name: t("modules.kds_order_report.field.created_at.title"),
				type: "date",
				typeOptions: ["bt"],
				isRemovable: false,
			},
			{
				id: "average_delivery_time",
				name: t("modules.kds_order_report.field.average_delivery_time.title"),
				type: "number",
			},
			{
				id: "order_number",
				name: t("common.word.order_number"),
				type: "text",
			},
			{
				id: "order_requested_pickup_at",
				name: t("modules.kds_order_report.field.order_requested_pickup_at.title"),
				type: "date",
				extraOptions: ["a", "u"],
			},
		];
	}

	static getKdsOrdersFilters(
		t: TFunction,
		groupReport?: boolean,
		filtersParams?: React.MutableRefObject<string>,
		type?: string
	): ListConfigFilter[] {
		return [
			{
				id: "employee_delivery",
				name: t("modules.kds_order_report.field.employee_delivery.title"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"EMPLOYEE",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getEmployeesSearchSelect(
									search,
									params,
									{
										cancelToken: options?.token,
									},
									t
								),
					  },
			},
			{
				id: "order_type",
				name: t("modules.kds_order_report.field.order_type.title"),
				type: "list",
				listOptions: {
					DINE_IN: t("enums.orders.types.DINE_IN"),
					DELIVERY: t("enums.orders.types.DELIVERY"),
					ROOM_SERVICE: t("enums.orders.types.ROOM_SERVICE"),
					PICK_UP: t("enums.orders.types.PICK_UP"),
				},
			},
			{
				id: "order_source",
				name: t("common.word.source", { ns: "lib" }),
				type: "text",
			},
			{
				id: "point_of_sale",
				name: t("common.word.point_of_sale"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"POINT_OF_SALE",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getPointsOfSaleSearchSelect(search, params, {
									cancelToken: options?.token,
								}),
					  },
			},
			{
				id: "comment",
				name: t("common.word.comment"),
				type: "text",
			},
			{
				id: "execution_at",
				name: t("modules.kds_order_report.field.execution_at.title"),
				type: "date",
			},
			{
				id: "average_acceptance_time",
				name: t("modules.kds_order_report.field.average_acceptance_time.title"),
				type: "number",
			},
			{
				id: "average_total_time",
				name: t("modules.kds_order_report.field.average_total_time.title"),
				type: "number",
			},
			{
				id: "average_receiving_time",
				name: t("modules.kds_order_report.field.average_receiving_time.title"),
				type: "number",
			},
			{
				id: "average_start_in_progress_time",
				name: t("modules.kds_order_report.field.average_start_in_progress_time.title"),
				type: "number",
			},
			{
				id: "average_start_ready_time",
				name: t("modules.kds_order_report.field.average_start_ready_time.title"),
				type: "number",
			},
			{
				id: "average_start_packed_time",
				name: t("modules.kds_order_report.field.average_start_packed_time.title"),
				type: "number",
			},
			{
				id: "average_start_received_time",
				name: t("modules.kds_order_report.field.average_start_received_time.title"),
				type: "number",
			},
			{
				id: "new_at",
				name: t("modules.kds_order_report.field.new_at.title"),
				type: "date",
			},
			{
				id: "in_progress_at",
				name: t("common.word.date_start_at"),
				type: "date",
			},
			{
				id: "ready_at",
				name: t("modules.kds_order_report.field.ready_at.title"),
				type: "date",
			},
			{
				id: "packed_at",
				name: t("modules.kds_order_report.field.packed_at.title"),
				type: "date",
			},
			{
				id: "received_at",
				name: t("modules.kds_order_report.field.received_at.title"),
				type: "date",
			},
			{
				id: "delivered_at",
				name: t("modules.kds_order_report.field.delivered_at.title"),
				type: "date",
			},
			{
				id: "preparation_status",
				name: t("modules.kds_order_report.field.preparation_status.title"),
				type: "list",
				listOptions: {
					DELIVERED: t("enums.preparation_statuses.DELIVERED"),
					RECEIVED: t("enums.preparation_statuses.RECEIVED"),
					PACKED: t("enums.preparation_statuses.PACKED"),
					READY: t("enums.preparation_statuses.READY"),
					IN_PROGRESS: t("enums.preparation_statuses.IN_PROGRESS"),
					NEW: t("enums.preparation_statuses.NEW"),
				},
			},
		];
	}

	static getKdsOrdersGroups(t: TFunction) {
		return [
			{
				id: "ORDER_CREATED_AT_DAY_OF_MONTH",
				name: t("common.word.day_of_month"),
			},
			{
				id: "ORDER_CREATED_AT_MONTH",
				name: t("lib:common.word.month"),
			},
			{
				id: "ORDER_CREATED_AT_HOUR_OF_DAY",
				name: t("common.word.hour", { ns: "lib" }),
			},
			{
				id: "ORDER_CREATED_AT",
				name: t("common.word.date", { ns: "lib" }),
			},
			{
				id: "ORDER_CREATED_AT_DAY_OF_WEEK",
				name: t("common.word.day_of_week"),
			},
			{
				id: "EXECUTION_AT",
				name: t("modules.kds_order_report.field.execution_at.title"),
			},
			{
				id: "TYPE",
				name: t("modules.kds_order_report.field.order_type.title"),
			},
			{
				id: "SOURCE",
				name: t("common.word.source", { ns: "lib" }),
			},
			{
				id: "POINT_OF_SALE",
				name: t("common.word.point_of_sale"),
			},
			{
				id: "COMMENT",
				name: t("common.word.comment"),
			},
			{
				id: "EMPLOYEE_DELIVERY",
				name: t("modules.kds_order_report.field.employee_delivery.title"),
			},
			{
				id: "DELIVERY_ZONE",
				name: t("modules.kds_order_report.field.delivery_zone.title"),
			},
			{
				id: "POINT_OF_SALE",
				name: t("common.word.point_of_sale"),
			},
			{
				id: "NUMBER",
				name: t("common.word.order_number"),
			},
			{
				id: "PREPARATION_STATUS",
				name: t("modules.kds_order_report.field.preparation_status.title"),
			},
		];
	}

	static getKdsOrdersGroupsInfo(t: TFunction, organization?: OrganizationState) {
		return {
			POINT_OF_SALE: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link
							to={`/${organization.id}/settings/rooms/point-of-sales/${groupByValue.id}`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}
				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			EMPLOYEE_DELIVERY: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link to={`/${organization.id}/employees/${groupByValue.id}`} rel="noreferrer" target="_blank">
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}
				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			ORDER_CREATED_AT_MONTH: (groupByValue: Record<string, any>) => getMonthName(groupByValue.name, t),
			ORDER_CREATED_AT_DAY_OF_WEEK: (groupByValue: Record<string, any>) => getDayName(groupByValue.name, t),
			ORDER_CREATED_AT: (groupByValue: Record<string, any>) => {
				const date = new Date(Number(groupByValue.name));
				return date.toLocaleDateString();
			},
			TYPE: (groupByValue: Record<string, any>) => t(`enums.orders.types.${groupByValue.name}`),
			PREPARATION_STATUS: (groupByValue: Record<string, any>) => {
				const { name } = groupByValue;

				if (name === "empty") return "-";

				return t(`enums.preparation_statuses.${name}`);
			},
		};
	}

	static getKdsGroups(t: TFunction) {
		return [
			{
				id: "DIRECTION",
				name: t("components.reports.groups.DIRECTION"),
			},
			{
				id: "PRODUCT",
				name: t("components.reports.groups.ITEM"),
			},
			{
				id: "CATEGORY",
				name: t("components.reports.groups.CATEGORY"),
			},
			{
				id: "ORDER_ITEM_CREATED_AT_HOUR",
				name: t("components.reports.groups.ORDER_ITEM_CREATED_AT_HOUR"),
			},
			{
				id: "ORDER_NUMBER",
				name: t("components.reports.groups.ORDER_NUMBER"),
			},
			{
				id: "ORDER_ITEM_CREATED_AT_DAY",
				name: t("components.reports.groups.ORDER_ITEM_CREATED_AT_DAY"),
			},
			{
				id: "ORDER_ITEM_CREATED_AT_MONTH",
				name: t("components.reports.groups.ORDER_ITEM_CREATED_AT_MONTH"),
			},
			{
				id: "ORDER_ITEM_CREATED_AT_DAY_OF_WEEK",
				name: t("components.reports.groups.ORDER_ITEM_CREATED_AT_DAY_OF_WEEK"),
			},
			{
				id: "ORDER_ITEM_ID",
				name: t("components.reports.groups.ORDER_ITEM_ID"),
			},
		];
	}

	static getKdsFilters(
		t: TFunction,
		groupReport?: boolean,
		filtersParams?: React.MutableRefObject<string>,
		type?: string
	) {
		return [
			{
				id: "product",
				name: t("common.word.item_group_variant"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"PRODUCT",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getItemsSearchSelect(search, params, {
									cancelToken: options?.token,
								}),
					  },
			},
			{
				id: "category",
				name: t("common.word.item_group_category"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"CATEGORY",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getCategoriesSearchSelect(search, params, {
									cancelToken: options?.token,
								}),
					  },
			},
			{
				id: "direction",
				name: t("common.word.direction"),
				type: groupReport ? "label_search_select" : "search_select",
				source: groupReport
					? {
							request: (queryString?: string, params?: Record<string, any>) =>
								getReportFilter(
									apiReport.getReportFilter,
									"DIRECTION",
									queryString,
									filtersParams?.current,
									type,
									undefined,
									params
								),
					  }
					: {
							request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
								apiOrganization.getDirectionsSearchSelect(search, params, {
									cancelToken: options?.token,
								}),
					  },
			},
			{
				id: "order_item_created_at",
				name: t("modules.preparation_status_report.field.order_item_created_at.title"),
				type: "date",
			},
			{
				id: "average_new_in_progress_time",
				name: t("modules.kds_order_report.field.average_start_in_progress_time.title"),
				type: "number",
			},
			{
				id: "average_new_ready_time",
				name: t("modules.kds_order_report.field.average_start_ready_time.title"),
				type: "number",
			},
			{
				id: "average_new_packed_time",
				name: t("modules.kds_order_report.field.average_start_packed_time.title"),
				type: "number",
			},
			{
				id: "average_new_received_time",
				name: t("modules.kds_order_report.field.average_start_received_time.title"),
				type: "number",
			},
		];
	}

	static getKdsGroupsInfo(t: TFunction, organization?: OrganizationState) {
		return {
			DIRECTION: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link
							to={`/${organization.id}/settings/rooms/directions/${groupByValue.id}`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}
				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			CATEGORY: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link
							to={`/${organization.id}/menu/categories/${groupByValue.id}`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}
				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			PRODUCT: (groupByValue: Record<string, any>) => {
				if (organization) {
					return groupByValue.id !== "null" ? (
						<Link
							to={`/${organization.id}/menu/item_groups/${groupByValue.id}`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
					) : (
						"-"
					);
				}
				return groupByValue.id !== "null" ? groupByValue.name : "-";
			},
			ORDER_ITEM_CREATED_AT_DAY_OF_WEEK: (groupByValue: Record<string, any>) => {
				return getDayName(groupByValue.name, t);
			},
			ORDER_ITEM_CREATED_AT_MONTH: (groupByValue: Record<string, any>) => getMonthName(groupByValue.name, t),
			ORDER_ITEM_ID: (groupByValue: Record<string, any>) => `#${groupByValue.name}`,
		};
	}

	static getKdsOrdersColumns(t: TFunction) {
		return [
			{
				id: "average_start_in_progress_time",
				name: t("modules.kds_order_report.field.average_start_in_progress_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(
					t,
					"start_in_progress_time",
					"start_in_progress_time_count"
				),
			},
			{
				id: "average_start_ready_time",
				name: t("modules.kds_order_report.field.average_start_ready_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "start_ready_time", "start_ready_time_count"),
			},
			{
				id: "average_start_packed_time",
				name: t("modules.kds_order_report.field.average_start_packed_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "start_packed_time", "start_packed_time_count"),
			},
			{
				id: "average_start_received_time",
				name: t("modules.kds_order_report.field.average_start_received_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(
					t,
					"start_received_time",
					"start_received_time_count"
				),
			},
		];
	}

	static getKdsColumns(t: TFunction) {
		return [
			{
				id: "average_new_in_progress_time",
				name: t("modules.kds_order_report.field.average_start_in_progress_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "new_in_progress_time", "new_in_progress_count"),
			},
			{
				id: "average_new_ready_time",
				name: t("modules.kds_order_report.field.average_start_ready_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "new_ready_time", "new_ready_time_count"),
			},
			{
				id: "average_new_packed_time",
				name: t("modules.kds_order_report.field.average_start_packed_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "new_packed_time", "new_packed_time_count"),
			},
			{
				id: "average_new_received_time",
				name: t("modules.kds_order_report.field.average_start_received_time.title"),
				...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "new_received_time", "new_received_time_count"),
			},
		];
	}
}
